<template>
  <!-- eslint-disable max-len -->
  <div
    v-if="localShow"
    ref="modal"
    class="backdrop"
    :style="{ 'z-index': zIndex }"
    tabindex="-1"
    @mousedown="mousedown"
    @mouseup="mouseup"
    @keyup.esc="handleEscapeKey"
  >
    <div
      class="modal"
      :class="{'full-screen': fullScreen, pop: isPopping}"
      :style="{ width: width, height: height }"
      @click.stop=""
    >
      <div class="modal-header">
        <slot name="modal-header-prepend" />
        <h2 v-if="title" :title="title" class="mt-0 mb-0">{{ title }}</h2>
        <slot v-else name="modal-header-title-description" />
        <slot name="modal-header" />
        <IconButton
          class="close-modal-btn"
          :icon="'close'"
          :width="28"
          :height="28"
          @click="closeModal"
        />
      </div>
      <div class="modal-container scrollbar">
        <div class="modal-main">
          <slot name="modal-main" />
        </div>
      </div>
      <div class="modal-footer">
        <slot name="modal-footer" />
        <slot name="modal-actions" class="modal-actions" />
      </div>
    </div>
  </div>
</template>

<script>
import IconButton from '@/components/IconButton.vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { ref } from 'vue';

export default {
  name: 'Modal',
  components: {
    IconButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
    isPluginModal: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: undefined,
    },
    height: {
      type: String,
      default: undefined,
    },
    padding: {
      type: String,
      default: '20px',
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
    closeWithEscapeKey: {
      type: Boolean,
      default: true,
    },
    zIndex: {
      type: Number,
      default: null,
    },
    persistent: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:show', 'opened', 'closed'],
  setup() {
    const modal = ref(null);
    const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(modal, { immediate: false, allowOutsideClick: true });

    return {
      modal,
      activateFocusTrap,
      deactivateFocusTrap,
    };
  },
  data() {
    return {
      localShow: false,
      mousedownOnBackdrop: false,
      isPopping: false,
    };
  },
  watch: {
    show(shouldShow) {
      if (shouldShow && !this.localShow) {
        this.localShow = true;
      }
    },
    localShow(isShown) {
      if (isShown) {
        this.$nextTick(() => {
          this.activateFocusTrap();
          this.modal.focus();
        });
      } else {
        this.$nextTick(() => {
          this.deactivateFocusTrap();
        });
      }
      this.$emit('update:show', isShown);
    },
  },
  mounted() {
    if (this.show && !this.localShow) {
      this.localShow = true;
    }
  },
  methods: {
    showModal() {
      this.localShow = true;
      this.$emit('opened');
      this.$nextTick(() => {
        this.$refs.modal.focus();
      });
    },
    focus() {
      this.$nextTick(() => {
        this.$refs.modal.focus();
      });
    },
    closeModal() {
      this.localShow = false;
      this.$emit('closed');
    },
    handleEscapeKey() {
      if (this.closeWithEscapeKey) {
        this.closeModal();
      }
    },
    mousedown(e) {
      this.mousedownOnBackdrop = Boolean(e.srcElement === this.$refs.modal);
    },
    mouseup(e) {
      // Prevents drag originating from the modal to the backdrop from closing it
      const isDescendantOfModal = this.$refs.modal.contains(e.target);
      if (isDescendantOfModal && this.mousedownOnBackdrop) {
        if (this.persistent) {
          this.startPopAnimation();
        } else {
          this.closeModal();
        }
      }
      this.mousedownOnBackdrop = false;
    },
    startPopAnimation() {
      this.isPopping = true;
      setTimeout(() => {
        this.isPopping = false;
      }, 150); // Stop the wiggle after 1 second
    },
  },
};
</script>

<style lang="scss" scoped>
$mainPadding: 24px;
$mainPaddingMobile: 16px;

</style>
